import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ArrowLeft, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { useSearch } from '../../contexts/SearchContext';
import { SearchResults } from './SearchResults';

interface MobileSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileSearchModal({ isOpen, onClose }: MobileSearchModalProps) {
  const { query, setQuery, clearSearch, performSearch } = useSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['project', 'creative', 'analysis']);
  const [sortBy, setSortBy] = useState<'relevance' | 'date'>('relevance');

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle touch gestures for closing
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      isDragging = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;

      // Only allow downward swipe from the top portion of the modal
      if (deltaY > 0 && startY < 100) {
        e.preventDefault();
        if (modalRef.current) {
          modalRef.current.style.transform = `translateY(${Math.min(deltaY, 100)}px)`;
          modalRef.current.style.opacity = `${Math.max(1 - deltaY / 200, 0.5)}`;
        }
      }
    };

    const handleTouchEnd = () => {
      if (!isDragging) return;
      isDragging = false;

      const deltaY = currentY - startY;
      
      if (modalRef.current) {
        if (deltaY > 50) {
          // Close modal if swiped down enough
          onClose();
        } else {
          // Reset position
          modalRef.current.style.transform = 'translateY(0)';
          modalRef.current.style.opacity = '1';
        }
      }
    };

    const modal = modalRef.current;
    modal.addEventListener('touchstart', handleTouchStart, { passive: false });
    modal.addEventListener('touchmove', handleTouchMove, { passive: false });
    modal.addEventListener('touchend', handleTouchEnd);

    return () => {
      modal.removeEventListener('touchstart', handleTouchStart);
      modal.removeEventListener('touchmove', handleTouchMove);
      modal.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      // Perform search with filters
      performSearch({
        query: query.trim(),
        types: selectedTypes as ('project' | 'creative' | 'analysis')[],
      });
    }
  };

  const handleClear = () => {
    clearSearch();
    inputRef.current?.focus();
  };

  const handleFilterChange = (type: string, checked: boolean) => {
    if (checked && !selectedTypes.includes(type)) {
      setSelectedTypes(prev => [...prev, type]);
    } else if (!checked && selectedTypes.includes(type)) {
      setSelectedTypes(prev => prev.filter(t => t !== type));
    }
  };

  // Perform search when filters change
  useEffect(() => {
    if (query.trim() && isOpen) {
      performSearch({
        query: query.trim(),
        types: selectedTypes as ('project' | 'creative' | 'analysis')[],
      });
    }
  }, [selectedTypes, sortBy, performSearch, query, isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black bg-opacity-50"
          onClick={onClose}
        >
          <motion.div
            ref={modalRef}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="absolute inset-0 bg-white flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <ArrowLeft className="w-6 h-6 text-gray-600" />
                </button>
                
                <form onSubmit={handleSubmit} className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search creatives, projects, analyses..."
                      className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base min-h-[44px]"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                    />
                    {query && (
                      <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    )}
                  </div>
                </form>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ${
                    showFilters 
                      ? 'bg-purple-50 text-purple-700' 
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <Filter className="w-5 h-5" />
                </button>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="space-y-6">
                    {/* Content Types */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Content Types</h3>
                      <div className="grid grid-cols-1 gap-3">
                        {[
                          { id: 'project', label: 'Projects' },
                          { id: 'creative', label: 'Creatives' },
                          { id: 'analysis', label: 'Analyses' }
                        ].map((type) => (
                          <label key={type.id} className="flex items-center min-h-[44px]">
                            <input
                              type="checkbox"
                              checked={selectedTypes.includes(type.id)}
                              onChange={(e) => handleFilterChange(type.id, e.target.checked)}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 w-4 h-4"
                            />
                            <span className="ml-3 text-sm text-gray-700">{type.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Sort By */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Sort By</h3>
                      <div className="grid grid-cols-1 gap-3">
                        {[
                          { id: 'relevance', label: 'Relevance' },
                          { id: 'date', label: 'Date Modified' }
                        ].map((option) => (
                          <label key={option.id} className="flex items-center min-h-[44px]">
                            <input
                              type="radio"
                              name="sortBy"
                              value={option.id}
                              checked={sortBy === option.id}
                              onChange={(e) => setSortBy(e.target.value as 'relevance' | 'date')}
                              className="border-gray-300 text-purple-600 focus:ring-purple-500 w-4 h-4"
                            />
                            <span className="ml-3 text-sm text-gray-700">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              <SearchResults
                isDropdown={false}
                showLoadMore={true}
                onClose={onClose}
                hideFilters={true}
              />
            </div>

            {/* Swipe indicator */}
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
              <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}